import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axiosInstance from '../../config/axiosApi/axiosApi';
import APIs from '../../Constants/apis';
import Preloader from '../../components/CustomComponents/preLoad/preLoader';
import showErrorToast, { showSuccessToast } from '../../components/CustomComponents/toast/toast';
import useDarkMode from '../../hooks/useDarkMode';
import moment from 'moment';
import swal from 'sweetalert';
import CustomModal from '../../components/CustomComponents/modal/customModal';

const StaffPayoutHistory = () => {
  const { userName: userNameParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const CompID = useSelector((state) => state.auth.user?.userData?.compID);
  const { profileData } = useSelector((state) => state.auth);
  const isDarkMode = useDarkMode();

  const searchParams = new URLSearchParams(location.search);

  const [loading, setLoading] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [staffInfo, setStaffInfo] = useState(null);
  const [showProcessModal, setShowProcessModal] = useState(false);
  const [processingPayoutID, setProcessingPayoutID] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    paymentMethod: '',
    paymentReference: '',
    paymentNotes: ''
  });
  const [compCurrency, setCompCurrency] = useState("GBP");
  const [expandedPayouts, setExpandedPayouts] = useState(new Set());
  const [pendingExpandId, setPendingExpandId] = useState(searchParams.get('payoutId'));
  const queryUserName = searchParams.get('userName');
  const effectiveUserName = userNameParam || queryUserName || '';

  // Currency symbol mapping
  const currencySymbols = {
    GBP: "£",
    USD: "$",
    EUR: "€",
    PKR: "₨",
    Rs: "₨",
  };

  const getCurrencySymbol = (currencyCode) => {
    const normalized = (currencyCode || "GBP").toUpperCase();
    return currencySymbols[normalized] || currencySymbols["GBP"];
  };

  const normalizeCurrencyCode = (code) => {
    const normalized = (code || "GBP").toUpperCase();
    if (normalized === "RS") return "PKR";
    return ["GBP", "USD", "EUR", "PKR"].includes(normalized) ? normalized : "GBP";
  };

  const formatCurrency = (amount) => {
    try {
      const normalizedCurrency = normalizeCurrencyCode(compCurrency);
      const locale = normalizedCurrency === 'USD' ? 'en-US' 
                   : normalizedCurrency === 'EUR' ? 'en-EU' 
                   : normalizedCurrency === 'PKR' ? 'en-PK'
                   : 'en-GB';
      
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency: normalizedCurrency
      }).format(amount || 0);
    } catch (error) {
      const symbol = getCurrencySymbol(compCurrency);
      return `${symbol}${(amount || 0).toFixed(2)}`;
    }
  };

  useEffect(() => {
    // Fetch company currency from profile data
    if (profileData?.compCurrency) {
      const normalized = normalizeCurrencyCode(profileData.compCurrency);
      setCompCurrency(normalized);
    }
  }, [profileData]);

  useEffect(() => {
    setPendingExpandId(searchParams.get('payoutId'));
  }, [location.search]);

  useEffect(() => {
    const loadData = async () => {
      if (!CompID) {
        return;
      }

      setLoading(true);
      try {
        await Promise.all([
          fetchPayouts(effectiveUserName),
          effectiveUserName ? fetchStaffInfo(effectiveUserName) : Promise.resolve(null)
        ]);

        if (!effectiveUserName) {
          setStaffInfo(null);
        }
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [CompID, effectiveUserName]);

  useEffect(() => {
    if (!pendingExpandId || !payouts.length) {
      return;
    }

    const matchingItem = payouts.find((item) => {
      const payout = item.payout || item;
      const payoutId = payout.payout_ID || payout.Payout_ID;
      return payoutId && payoutId.toString() === pendingExpandId;
    });

    if (matchingItem) {
      const payout = matchingItem.payout || matchingItem;
      const payoutId = payout.payout_ID || payout.Payout_ID;
      setExpandedPayouts((prev) => {
        const newSet = new Set(prev);
        newSet.add(payoutId);
        return newSet;
      });
      setPendingExpandId(null);
    }
  }, [pendingExpandId, payouts]);

  const handleToggleDetails = (payout) => {
    const payoutId = payout.payout_ID || payout.Payout_ID;
    if (!payoutId) return;

    setExpandedPayouts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(payoutId)) {
        newSet.delete(payoutId);
      } else {
        newSet.add(payoutId);
      }
      return newSet;
    });
  };

  const fetchStaffInfo = async (targetUserName) => {
    if (!targetUserName) {
      return;
    }
    try {
      const response = await axiosInstance.get(APIs.GET_STAFFS, {
        params: {
          compID: CompID,
          PageSize: 1,
          PageNumber: 1,
          searchQuery: targetUserName
        }
      });
      console.log('Staff info response:', response.data);
      if (response.data?.staffs?.length > 0) {
        setStaffInfo(response.data.staffs[0]);
      }
    } catch (error) {
      console.error('Error fetching staff info:', error);
      showErrorToast('Failed to fetch staff information');
    }
  };

  const fetchPayouts = async (targetUserName) => {
    try {
      const response = await axiosInstance.get(APIs.GET_PAYOUTS, {
        params: {
          compID: CompID,
          ...(targetUserName ? { userName: targetUserName } : {})
        }
      });

      // Handle nested response structure
      const payoutsData = response.data?.data || response.data || [];
      
      // Ensure it's an array
      const payoutsArray = Array.isArray(payoutsData) ? payoutsData : [];

      setPayouts(payoutsArray);
    } catch (error) {
      console.error('Error fetching payouts:', error);
      showErrorToast('Failed to fetch payouts');
      setPayouts([]); 
    }
  };

  const handleApprovePayout = async (payoutID) => {
    const result = await swal({
      title: "Approve Payout?",
      text: "Are you sure you want to approve this payout?",
      icon: "warning",
      buttons: ["Cancel", "Approve"],
      dangerMode: false,
    });

    if (result) {
      try {
        await axiosInstance.post(APIs.APPROVE_PAYOUT, {
          compID: CompID,
          payout_ID: payoutID
        });
        showSuccessToast('Payout approved successfully');
        fetchPayouts();
      } catch (error) {
        console.error('Error approving payout:', error);
        showErrorToast(error.response?.data?.message || 'Failed to approve payout');
      }
    }
  };

  const handleProcessPayout = (payoutID) => {
    setProcessingPayoutID(payoutID);
    setShowProcessModal(true);
    setPaymentForm({
      paymentMethod: '',
      paymentReference: '',
      paymentNotes: ''
    });
  };

  const confirmProcessPayout = async () => {
    if (!paymentForm.paymentMethod) {
      showErrorToast('Please select a payment method');
      return;
    }

    try {
      await axiosInstance.post(APIs.PROCESS_PAYOUT, {
        compID: CompID,
        payout_ID: processingPayoutID,
        payment_Method: paymentForm.paymentMethod,
        payment_Reference: paymentForm.paymentReference,
        payment_Notes: paymentForm.paymentNotes
      });
      showSuccessToast('Payment processed successfully');
      setShowProcessModal(false);
      setProcessingPayoutID(null);
      fetchPayouts();
    } catch (error) {
      console.error('Error processing payment:', error);
      showErrorToast(error.response?.data?.message || 'Failed to process payment');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status?.toUpperCase()) {
      case 'PENDING': return 'bg-warning';
      case 'APPROVED': return 'bg-info';
      case 'PAID': return 'bg-success';
      case 'CANCELLED': return 'bg-danger';
      default: return 'bg-secondary';
    }
  };

  if (loading) {
    return <Preloader show={loading} />;
  }

  return (
    <div>
      
      {/* Page Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h1 className="page-header mb-2">
            <i className="fa fa-wallet me-2"></i>
            Payout History
          </h1>
          {staffInfo && (
            <div className="text-muted">
              <i className="fa fa-user me-2"></i>
              {staffInfo.FirstName} {staffInfo.LastName} ({staffInfo.UserName})
            </div>
          )}
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate('/team/staffShiftReport')}
        >
          <i className="fa fa-arrow-left me-2"></i>
          Back to Work Report
        </button>
      </div>

      {/* Payouts Table */}
      <div className="card">
        <div className="card-body">
          {payouts.length === 0 ? (
            <div className="text-center py-5">
              <i className="fa fa-money-bill-wave fa-3x text-muted mb-3 d-block"></i>
              <h5>No Payouts Found</h5>
              <p className="text-muted">No payout records available for this staff member</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="bg-light">
                  <tr>
                    {!effectiveUserName && <th>Staff Member</th>}
                    <th>Period</th>
                    <th className="text-center">Type</th>
                    <th className="text-center">Working Hours</th>
                    <th className="text-center">Regular</th>
                    <th className="text-center">Overtime</th>
                    <th className="text-end">Net Amount</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Actions</th>
                    <th className="text-center"></th>
                  </tr>
                </thead>
                <tbody>
                  {payouts.map((item) => {
                    const payout = item.payout || item;
                    const payoutId = payout.payout_ID || payout.Payout_ID;
                    const isExpanded = expandedPayouts.has(payoutId);
                    const staffName = `${payout.staff_FirstName || payout.StaffFirstName || payout.firstName || payout.FirstName || ''} ${payout.staff_LastName || payout.StaffLastName || payout.lastName || payout.LastName || ''}`.trim();
                    const payoutUserName = payout.userName || payout.UserName || payout.staff_UserName || '';
                    return (
                      <React.Fragment key={payoutId}>
                        <tr className={isExpanded ? 'table-active' : ''}>
                        {!effectiveUserName && (
                          <td>
                            <div className="fw-semibold">{staffName || payoutUserName || 'Unknown Staff'}</div>
                            {payoutUserName && (
                              <div className="text-muted small">{payoutUserName}</div>
                            )}
                          </td>
                        )}
                        <td>
                          <div className="fw-semibold">
                            {moment(payout.period_StartDate || payout.Period_StartDate).format('MMM DD')} - {moment(payout.period_EndDate || payout.Period_EndDate).format('MMM DD, YYYY')}
                          </div>
                          <div className="text-muted small">
                            Created: {moment(payout.createdDateTime || payout.CreatedDateTime).format('MMM DD, YYYY')}
                          </div>
                        </td>
                        <td className="text-center">
                          <span className="badge bg-secondary">
                            {payout.payout_Type || payout.Payout_Type}
                          </span>
                        </td>
                        <td className="text-center">
                          <span className="fw-semibold">
                            {(payout.total_Working_Hours || payout.Total_Working_Hours || 0).toFixed(1)} hrs
                          </span>
                        </td>
                        <td className="text-center">
                          {(payout.total_Regular_Hours || payout.Total_Regular_Hours || 0).toFixed(1)} hrs
                        </td>
                        <td className="text-center">
                          {(payout.total_Overtime_Hours || payout.Total_Overtime_Hours || 0).toFixed(1)} hrs
                        </td>
                        <td className="text-end">
                          <div className="fw-bold fs-6">
                            {formatCurrency(payout.net_Amount || payout.Net_Amount || 0)}
                          </div>
                        </td>
                        <td className="text-center">
                          <span className={`badge ${getStatusBadgeClass(payout.payout_Status || payout.Payout_Status)}`}>
                            {payout.payout_Status || payout.Payout_Status}
                          </span>
                        </td>
                        <td className="text-center">
                          <div className="btn-group btn-group-sm">
                            {(payout.payout_Status || payout.Payout_Status) === 'PENDING' && effectiveUserName && (
                              <button
                                className="btn btn-sm btn-info"
                                onClick={() => handleApprovePayout(payout.payout_ID || payout.Payout_ID)}
                                title="Approve Payout"
                              >
                                <i className="fa fa-check me-1"></i>
                                Approve
                              </button>
                            )}
                            {(payout.payout_Status || payout.Payout_Status) === 'APPROVED' && effectiveUserName && (
                              <button
                                className="btn btn-sm btn-success"
                                onClick={() => handleProcessPayout(payout.payout_ID || payout.Payout_ID)}
                                title="Process Payment"
                              >
                                <i className="fa fa-money-bill me-1"></i>
                                Pay
                              </button>
                            )}
                            {!effectiveUserName && (
                              <button
                                className="btn btn-sm btn-outline-primary"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!effectiveUserName && payoutUserName) {
                                    const params = new URLSearchParams();
                                    params.set('payoutId', (payoutId || '').toString());
                                    navigate(`/team/payouts/${payoutUserName}?${params.toString()}`);
                                    return;
                                  }
                                  handleToggleDetails(payout);
                                }}
                                title="View Details"
                              >
                                <i className="fa fa-external-link-alt"></i>
                              </button>
                            )}
                          </div>
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-link btn-sm text-decoration-none text-muted"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleToggleDetails(payout);
                            }}
                          >
                            <i className={`fa fa-chevron-${isExpanded ? 'down' : 'right'}`}></i>
                          </button>
                        </td>
                      </tr>
                      {isExpanded && (
                        <tr key={`details-${payoutId}`}>
                          <td colSpan={effectiveUserName ? 9 : 10} className="p-0">
                            <div className="p-4 bg-light border-top">
                              <div className="d-flex justify-content-between align-items-center mb-3">
                                <h6 className="mb-0">Payout Details</h6>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    const newExpanded = new Set(expandedPayouts);
                                    newExpanded.delete(payoutId);
                                    setExpandedPayouts(newExpanded);
                                  }}
                                >
                                  <i className="fa fa-times me-1"></i>
                                  Close
                                </button>
                              </div>
                              
                              {(() => {
                                const totalWorkingHours = payout.total_Working_Hours || payout.Total_Working_Hours || 0;
                                const totalRegularHours = payout.total_Regular_Hours || payout.Total_Regular_Hours || 0;
                                const totalOvertimeHours = payout.total_Overtime_Hours || payout.Total_Overtime_Hours || 0;
                                const totalBreakHours = payout.total_Break_Hours || payout.Total_Break_Hours || 0;
                                const totalUnpaidBreakHours = payout.total_Unpaid_Break_Hours || payout.Total_Unpaid_Break_Hours || 0;
                                const totalPaidBreakHours = payout.total_Paid_Break_Hours || payout.Total_Paid_Break_Hours || 0;
                                const regularEarnings = payout.regular_Earnings || payout.Regular_Earnings || 0;
                                const overtimeEarnings = payout.overtime_Earnings || payout.Overtime_Earnings || 0;
                                const paidBreakEarnings = payout.paid_Break_Earnings || payout.Paid_Break_Earnings || 0;
                                const bonusAmount = payout.bonus_Amount || payout.Bonus_Amount || 0;
                                const deductionAmount = payout.deduction_Amount || payout.Deduction_Amount || 0;
                                const grossAmount = payout.gross_Amount || payout.Gross_Amount || 0;
                                const taxAmount = payout.tax_Amount || payout.Tax_Amount || 0;
                                const netAmount = payout.net_Amount || payout.Net_Amount || 0;
                                
                                // Leave statistics
                                const totalLeaveHours = payout.total_Leave_Hours || payout.Total_Leave_Hours || 0;
                                const totalLeaveDays = payout.total_Leave_Days || payout.Total_Leave_Days || 0;
                                const leaveEarnings = payout.leave_Earnings || payout.Leave_Earnings || 0;
                                const leaveAdjustmentAmount = payout.leave_Adjustment_Amount || payout.Leave_Adjustment_Amount || 0;
                                
                                return (
                                  <>
                                    {/* Summary Cards */}
                                    <div className="row g-3 mb-3">
                                      <div className="col-md-3">
                                        <div className="card border-0 bg-primary text-white">
                                          <div className="card-body p-3">
                                            <div className="small">Working Hours</div>
                                            <div className="fs-4 fw-bold">{totalWorkingHours.toFixed(1)}</div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-3">
                                        <div className="card border-0 bg-success text-white">
                                          <div className="card-body p-3">
                                            <div className="small">Regular Hours</div>
                                            <div className="fs-4 fw-bold">{totalRegularHours.toFixed(1)}</div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-3">
                                        <div className="card border-0 bg-purple text-white">
                                          <div className="card-body p-3">
                                            <div className="small">Overtime Hours</div>
                                            <div className="fs-4 fw-bold">{totalOvertimeHours.toFixed(1)}</div>
                                          </div>
                                        </div>
                                      </div>
                                      <div className="col-md-3">
                                        <div className="card border-0 bg-info text-white">
                                          <div className="card-body p-3">
                                            <div className="small">Net Amount</div>
                                            <div className="fs-4 fw-bold">{formatCurrency(netAmount)}</div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                    
                                    {/* Earnings and Deductions Table */}
                                    <div className="card mb-3">
                                      <div className="card-body">
                                        <h6 className="mb-3">Earnings & Deductions</h6>
                                        <table className="table table-sm mb-0">
                                          <tbody>
                                            <tr>
                                              <td>Regular Earnings:</td>
                                              <td className="text-end">{formatCurrency(regularEarnings)}</td>
                                            </tr>
                                            <tr>
                                              <td>Overtime Earnings:</td>
                                              <td className="text-end">{formatCurrency(overtimeEarnings)}</td>
                                            </tr>
                                            {paidBreakEarnings > 0 && (
                                              <tr>
                                                <td>Paid Break Earnings:</td>
                                                <td className="text-end text-success">+{formatCurrency(paidBreakEarnings)}</td>
                                              </tr>
                                            )}
                                            {(totalLeaveHours > 0 || leaveEarnings > 0) && (
                                              <tr>
                                                <td>Leave Earnings:</td>
                                                <td className="text-end text-success">+{formatCurrency(leaveEarnings)}</td>
                                              </tr>
                                            )}
                                            <tr>
                                              <td>Bonus:</td>
                                              <td className="text-end text-success">+{formatCurrency(bonusAmount)}</td>
                                            </tr>
                                            <tr>
                                              <td>Deductions:</td>
                                              <td className="text-end text-danger">-{formatCurrency(deductionAmount)}</td>
                                            </tr>
                                            {leaveAdjustmentAmount !== 0 && (
                                              <tr>
                                                <td>Leave Adjustment:</td>
                                                <td className={`text-end ${leaveAdjustmentAmount < 0 ? 'text-danger' : 'text-success'}`}>
                                                  {leaveAdjustmentAmount < 0 ? '-' : '+'}{formatCurrency(Math.abs(leaveAdjustmentAmount))}
                                                </td>
                                              </tr>
                                            )}
                                            <tr className="fw-bold">
                                              <td>Gross Amount:</td>
                                              <td className="text-end">{formatCurrency(grossAmount)}</td>
                                            </tr>
                                            <tr>
                                              <td>Tax:</td>
                                              <td className="text-end text-danger">-{formatCurrency(taxAmount)}</td>
                                            </tr>
                                            <tr className="fw-bold border-top">
                                              <td className="fs-5">Net Amount:</td>
                                              <td className="text-end text-primary fs-5">{formatCurrency(netAmount)}</td>
                                            </tr>
                                          </tbody>
                                        </table>
                                      </div>
                                    </div>
                                    
                                    {/* Break Hours Breakdown */}
                                    {totalBreakHours > 0 && (
                                      <div className="card mb-3">
                                        <div className="card-body">
                                          <h6 className="mb-3">Break Hours Breakdown</h6>
                                          <table className="table table-sm mb-0">
                                            <tbody>
                                              <tr>
                                                <td>Total Break Hours:</td>
                                                <td className="text-end">{totalBreakHours.toFixed(1)} hrs</td>
                                              </tr>
                                              {totalPaidBreakHours > 0 && (
                                                <tr>
                                                  <td>Paid Break Hours:</td>
                                                  <td className="text-end text-success">{totalPaidBreakHours.toFixed(1)} hrs</td>
                                                </tr>
                                              )}
                                              {totalUnpaidBreakHours > 0 && (
                                                <tr>
                                                  <td>Unpaid Break Hours:</td>
                                                  <td className="text-end text-warning">{totalUnpaidBreakHours.toFixed(1)} hrs</td>
                                                </tr>
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Leave Statistics */}
                                    {(totalLeaveDays > 0 || totalLeaveHours > 0 || leaveEarnings > 0 || leaveAdjustmentAmount !== 0) && (
                                      <div className="card mb-3">
                                        <div className="card-body">
                                          <h6 className="mb-3">
                                            <i className="fa fa-calendar-times me-2"></i>
                                            Leave Statistics
                                          </h6>
                                          <table className="table table-sm mb-0">
                                            <tbody>
                                              {totalLeaveDays > 0 && (
                                                <tr>
                                                  <td>Total Leave Days:</td>
                                                  <td className="text-end">{totalLeaveDays.toFixed(1)} days</td>
                                                </tr>
                                              )}
                                              {totalLeaveHours > 0 && (
                                                <tr>
                                                  <td>Total Leave Hours:</td>
                                                  <td className="text-end">{totalLeaveHours.toFixed(1)} hrs</td>
                                                </tr>
                                              )}
                                              {leaveEarnings > 0 && (
                                                <tr>
                                                  <td>Leave Earnings:</td>
                                                  <td className="text-end text-success">+{formatCurrency(leaveEarnings)}</td>
                                                </tr>
                                              )}
                                              {leaveAdjustmentAmount !== 0 && (
                                                <tr>
                                                  <td>Leave Adjustment:</td>
                                                  <td className={`text-end ${leaveAdjustmentAmount < 0 ? 'text-danger' : 'text-success'}`}>
                                                    {leaveAdjustmentAmount < 0 ? '-' : '+'}{formatCurrency(Math.abs(leaveAdjustmentAmount))}
                                                  </td>
                                                </tr>
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    )}
                                    
                                    {/* Notes */}
                                    {payout.notes && (
                                      <div className="card">
                                        <div className="card-body">
                                          <strong>Notes:</strong>
                                          <p className="mb-0 mt-1">{payout.notes}</p>
                                        </div>
                                      </div>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Process Payment Modal */}
      <CustomModal
        isOpen={showProcessModal}
        onClose={() => setShowProcessModal(false)}
        title="Process Payment"
        modalSize="modal-md"
        showButton={true}
        submitButtonText="Process Payment"
        onSubmit={confirmProcessPayout}
        submitButtonDisabled={!paymentForm.paymentMethod}
      >
        <div>
          <div className="mb-3">
            <label className="form-label">Payment Method <span className="text-danger">*</span></label>
            <select
              className="form-select"
              value={paymentForm.paymentMethod}
              onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
              style={{
                backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#000000',
                borderColor: isDarkMode ? '#374151' : '#dee2e6'
              }}
            >
              <option value="">-- Select Payment Method --</option>
              <option value="BANK_TRANSFER">Bank Transfer</option>
              <option value="CASH">Cash</option>
              <option value="CHEQUE">Cheque</option>
              <option value="PAYPAL">PayPal</option>
            </select>
          </div>
          <div className="mb-3">
            <label className="form-label">Payment Reference / Transaction ID</label>
            <input
              type="text"
              className="form-control"
              value={paymentForm.paymentReference}
              onChange={(e) => setPaymentForm({ ...paymentForm, paymentReference: e.target.value })}
              placeholder="Enter reference number"
              style={{
                backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#000000',
                borderColor: isDarkMode ? '#374151' : '#dee2e6'
              }}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Notes</label>
            <textarea
              className="form-control"
              value={paymentForm.paymentNotes}
              onChange={(e) => setPaymentForm({ ...paymentForm, paymentNotes: e.target.value })}
              rows="3"
              placeholder="Enter any notes..."
              style={{
                backgroundColor: isDarkMode ? '#1f2937' : '#ffffff',
                color: isDarkMode ? '#ffffff' : '#000000',
                borderColor: isDarkMode ? '#374151' : '#dee2e6'
              }}
            ></textarea>
          </div>
        </div>
      </CustomModal>
    </div>
  );
};

export default StaffPayoutHistory;

